/**
 * Consumes the assistant's Server-Sent Events stream.
 *
 * This is the one place in the app that talks to the backend outside a
 * TanStack Query hook, and the contract says so explicitly: the reply is a
 * `text/event-stream`, and the only legitimate source of its URL is the
 * `stream_path` returned by `postAssistantMessage`. Nothing here builds a URL.
 *
 * Reconnection is the browser's job — `EventSource` honours the server's
 * `retry:` hint and replays `Last-Event-ID` — so a refresh or a dropped Wi-Fi
 * connection mid-answer resumes rather than losing the turn. Events are
 * deduplicated on `seq` because a replay re-delivers everything after the last
 * id the browser saw.
 */

import { useCallback, useEffect, useReducer, useRef } from "react";

import { logger } from "@/lib/logger";

import type {
  ApiErrorResponse,
  AssistantAccessCardEvent,
  AssistantCitation,
  AssistantDoneEvent,
  AssistantIncidentFormEvent,
  AssistantMessage,
  AssistantRequestFormEvent,
  AssistantStatusEvent,
  AssistantStreamEvent,
  AssistantStreamEventType,
  TicketRef,
} from "./contract";
import { classifySseErrorEvent } from "./sse-error-event";

/**
 * `request_form` is not in the generated `AssistantStreamEventType` union yet
 * (see the PENDING BACKEND note in `./contract`), so it is named once here and
 * the reducer widens the payload to reach it. Delete both when the schema
 * carries the event.
 */
const REQUEST_FORM_EVENT = "request_form";

/**
 * `incident_form` is the incident-side twin of `REQUEST_FORM_EVENT`, and is
 * pending the same way: not in the generated union, so it is named once here
 * and reached by widening the payload. Delete alongside it.
 */
const INCIDENT_FORM_EVENT = "incident_form";

/** Where the stream is in its lifecycle, for the UI to branch on. */
export type AssistantStreamPhase = "idle" | "connecting" | "streaming" | "done" | "error";

export interface AssistantStreamState {
  phase: AssistantStreamPhase;
  /** The current "Searching SharePoint…" activity, or null between states. */
  activity: AssistantStatusEvent | null;
  /** Prose accumulated from `text` deltas. Superseded by `message` on `done`. */
  text: string;
  citations: AssistantCitation[];
  ticketRefs: TicketRef[];
  /**
   * Offering forms the assistant asked the user to fill in, in arrival order.
   *
   * Unlike citations these are *not* reconciled from
   * `done.message`: the persisted message has nowhere to carry them, so the
   * form lives only for as long as the page does. `AskPage` keeps its own copy
   * against the message id so the card survives the live→persisted handover.
   */
  requestForms: AssistantRequestFormEvent[];
  /**
   * Prefilled incident reports the assistant asked the user to check and send.
   *
   * Stream-only for the same reason as `requestForms`: the persisted message
   * has nowhere to carry them, so `AskPage` keeps its own copy against the
   * message id.
   */
  incidentForms: AssistantIncidentFormEvent[];
  /**
   * "Start an access request" cards (6 Oct): with the access front door on,
   * the assistant's pick of a user-access offering arrives as one of these
   * instead of a form. Stream-only, like the forms.
   */
  accessCards: AssistantAccessCardEvent[];
  /** The final, authoritative message. Present only after `done`. */
  message: AssistantMessage | null;
  /** A server-sent `error` event, translated by its `code` like any other. */
  error: ApiErrorResponse | null;
  /**
   * Whether `error` was flagged recoverable. A recoverable error is cleared as
   * soon as the turn makes progress again; a fatal one stays until reset, so
   * the user still sees why the answer stopped.
   */
  errorRecoverable: boolean;
  /** True when the connection itself failed rather than the answer. */
  transportFailed: boolean;
}

const INITIAL_STATE: AssistantStreamState = {
  phase: "idle",
  activity: null,
  text: "",
  citations: [],
  ticketRefs: [],
  requestForms: [],
  incidentForms: [],
  accessCards: [],
  message: null,
  error: null,
  errorRecoverable: false,
  transportFailed: false,
};

type Action =
  | { type: "reset" }
  | { type: "open" }
  | { type: "event"; event: AssistantStreamEvent }
  | { type: "transport-failed" };

/** Keeps citations ordered by the server-assigned index and free of duplicates. */
function mergeCitations(
  existing: AssistantCitation[],
  incoming: AssistantCitation,
): AssistantCitation[] {
  if (existing.some((citation) => citation.index === incoming.index)) {
    return existing;
  }
  return [...existing, incoming].sort((a, b) => a.index - b.index);
}

/**
 * The patch that drops a recoverable error once the turn moves on.
 *
 * Returns an empty object when there is nothing to clear, so the common case
 * does not churn the state object.
 */
function clearedRecoverableError(state: AssistantStreamState): Partial<AssistantStreamState> {
  if (state.error === null || !state.errorRecoverable) {
    return {};
  }
  return { error: null, errorRecoverable: false };
}

function reducer(state: AssistantStreamState, action: Action): AssistantStreamState {
  switch (action.type) {
    case "reset":
      return INITIAL_STATE;

    case "open":
      return { ...INITIAL_STATE, phase: "connecting" };

    case "transport-failed":
      // A `done` already landed: the answer is complete and the socket simply
      // closed afterwards. That is not a failure the user needs to see.
      return state.phase === "done" ? state : { ...state, phase: "error", transportFailed: true };

    case "event": {
      // Discriminate on the payload's own `type`, not the envelope's: the two
      // always agree on the wire, and only the inner one narrows `data` for the
      // generated union.
      const payload = action.event.data;
      switch (payload.type) {
        case "status":
          return {
            ...state,
            ...clearedRecoverableError(state),
            phase: "streaming",
            activity: payload,
          };
        case "text":
          return {
            ...state,
            ...clearedRecoverableError(state),
            phase: "streaming",
            text: state.text + payload.delta,
          };
        case "citation":
          return { ...state, citations: mergeCitations(state.citations, payload.citation) };
        case "ticket_ref":
          return state.ticketRefs.some((ticket) => ticket.ticket_id === payload.ticket.ticket_id)
            ? state
            : { ...state, ticketRefs: [...state.ticketRefs, payload.ticket] };
        case "error":
          return {
            ...state,
            error: payload.error,
            errorRecoverable: payload.recoverable ?? false,
            phase: payload.recoverable ? state.phase : "error",
            activity: null,
          };
        case "done":
          return {
            ...state,
            // A recoverable error the turn went on to survive must not sit
            // under a finished, correct answer with a Retry button — the user
            // would re-ask a question that already worked.
            ...clearedRecoverableError(state),
            phase: "done",
            activity: null,
            message: payload.message,
            // `done.message` is authoritative — reconcile any delta drift to it.
            text: payload.message.content.text,
            // The list fields carry Pydantic defaults, so the wire may omit them
            // entirely; an absent list means "none", never "keep the old one".
            citations: payload.message.citations ?? [],
            ticketRefs: payload.message.ticket_refs ?? [],
          };
        case "access_card": {
          // A replay re-delivers it; one card per distinct prefill.
          const fingerprint = JSON.stringify(payload);
          return state.accessCards.some((existing) => JSON.stringify(existing) === fingerprint)
            ? state
            : { ...state, accessCards: [...state.accessCards, payload] };
        }
        default: {
          // The generated union knows neither `request_form` nor
          // `incident_form` yet, so both land here rather than in a case of
          // their own.
          const name = (payload as { type?: string }).type;

          if (name === REQUEST_FORM_EVENT) {
            // Deduplicated on the offering, because a replayed stream
            // re-delivers the event and two identical forms in one answer
            // would be a bug either way.
            const form = payload as unknown as AssistantRequestFormEvent;
            return state.requestForms.some((existing) => existing.offering_id === form.offering_id)
              ? state
              : { ...state, requestForms: [...state.requestForms, form] };
          }

          if (name === INCIDENT_FORM_EVENT) {
            // An incident form has no offering to key on — it is prefill, not a
            // catalog item — so identity is the payload itself. That still
            // stops a replay adding a second copy, while leaving room for the
            // assistant to genuinely offer two different reports in one turn.
            const form = payload as unknown as AssistantIncidentFormEvent;
            const fingerprint = JSON.stringify(form);
            return state.incidentForms.some((existing) => JSON.stringify(existing) === fingerprint)
              ? state
              : { ...state, incidentForms: [...state.incidentForms, form] };
          }

          return state;
        }
      }
    }

    default:
      return state;
  }
}

/**
 * Every event name the contract defines, except `error`.
 *
 * `error` is deliberately absent: EventSource fires its own transport `error`
 * on the same name, so that one gets a dedicated listener that tells the two
 * apart (see `classifySseErrorEvent`).
 */
const EVENT_NAMES: readonly (
  | AssistantStreamEventType
  | typeof REQUEST_FORM_EVENT
  | typeof INCIDENT_FORM_EVENT
)[] = [
  "status",
  "text",
  "citation",
  "ticket_ref",
  "access_card",
  "done",
  REQUEST_FORM_EVENT,
  INCIDENT_FORM_EVENT,
];

function parseEnvelope(raw: string): AssistantStreamEvent | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed === "object" && parsed !== null && "type" in parsed && "data" in parsed) {
      return parsed as AssistantStreamEvent;
    }
    logger.warn("[assistant-stream] event payload missing type/data", parsed);
    return null;
  } catch (error) {
    logger.warn("[assistant-stream] unparseable event payload", error);
    return null;
  }
}

export interface UseAssistantStreamResult extends AssistantStreamState {
  /** True while the connection is open and the answer is still arriving. */
  isStreaming: boolean;
  /** Clears the accumulated turn — call before starting the next question. */
  reset: () => void;
}

/**
 * Opens `streamPath` and accumulates the turn.
 *
 * Pass `null` to hold the connection closed. Changing the path tears the old
 * connection down and starts fresh, so re-asking never mixes two answers.
 */
export function useAssistantStream(streamPath: string | null): UseAssistantStreamResult {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const seenSeq = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (streamPath === null) {
      dispatch({ type: "reset" });
      return undefined;
    }

    seenSeq.current = new Set();
    dispatch({ type: "open" });
    logger.debug("[assistant-stream] opening", streamPath);

    const source = new EventSource(streamPath, { withCredentials: true });
    let closed = false;

    // Set once the server sent a non-recoverable `error`: the turn is over
    // even if no `done` follows, so the socket the server then closes must not
    // be reconnected (the replay would be empty, and the loop endless).
    let terminalError = false;

    const finish = () => {
      closed = true;
      source.close();
    };

    const handle = (raw: string) => {
      const event = parseEnvelope(raw);
      if (event === null) {
        return;
      }
      // A reconnect replays from the last id the browser acknowledged, so the
      // same seq can arrive twice. Applying a text delta twice would duplicate
      // a sentence in the visible answer.
      if (typeof event.seq === "number") {
        if (seenSeq.current.has(event.seq)) {
          return;
        }
        seenSeq.current.add(event.seq);
      }
      dispatch({ type: "event", event });

      if (event.type === "error") {
        const recoverable = (event.data as { recoverable?: boolean }).recoverable ?? false;
        logger.debug("[assistant-stream] server error event", { recoverable });
        if (!recoverable) {
          terminalError = true;
        }
      }

      if (event.type === "done") {
        finish();
        logger.debug("[assistant-stream] done", (event.data as AssistantDoneEvent).message.status);
      }
    };

    const listeners = EVENT_NAMES.map((name) => {
      const listener = (messageEvent: MessageEvent<string>) => handle(messageEvent.data);
      source.addEventListener(name, listener);
      return { name, listener } as const;
    });

    // Servers that emit the envelope without an `event:` line land here.
    const defaultListener = (messageEvent: MessageEvent<string>) => handle(messageEvent.data);
    source.addEventListener("message", defaultListener);

    // One listener for both meanings of `error`: the server's named event (a
    // MessageEvent with data) and EventSource's transport error (no data).
    const errorListener = (event: Event) => {
      const classified = classifySseErrorEvent(event);
      if (classified.kind === "server") {
        handle(classified.data);
        return;
      }
      if (closed) {
        return;
      }
      if (terminalError) {
        // The server already said why the turn ended; the socket closing
        // afterwards is expected, not a second failure.
        finish();
        return;
      }
      // readyState CONNECTING means EventSource is retrying on its own; only a
      // CLOSED socket is terminal (a non-2xx response, or our own close above).
      if (source.readyState === EventSource.CONNECTING) {
        return;
      }
      logger.warn("[assistant-stream] connection closed by the browser");
      dispatch({ type: "transport-failed" });
    };
    source.addEventListener("error", errorListener);

    return () => {
      closed = true;
      for (const { name, listener } of listeners) {
        source.removeEventListener(name, listener);
      }
      source.removeEventListener("message", defaultListener);
      source.removeEventListener("error", errorListener);
      source.close();
      logger.debug("[assistant-stream] closed", streamPath);
    };
  }, [streamPath]);

  const reset = useCallback(() => {
    seenSeq.current = new Set();
    dispatch({ type: "reset" });
  }, []);

  return {
    ...state,
    isStreaming: state.phase === "connecting" || state.phase === "streaming",
    reset,
  };
}
